import {
  HeadingH2,
  Text_16_24,
  Text_24_36,
  Text_40_50,
} from '@dreamstack/feature-components/src/layout/Texts'
import {
  useFormatToCurrencyCutoff,
  useFormatToDecimal,
  useTranslation,
} from '@dreamstack/i18n'
import { ArrowDownRight } from '@dreamstack/icons/src/icon-components/ArrowDownRight'
import { ArrowUpRight } from '@dreamstack/icons/src/icon-components/ArrowUpRight'
import { SimpleTabs } from '@dreamstack/simple-components/src/lib/SimpleTabs'
import { clsxMerge } from '@dreamstack/util'
import { cn } from '@dreamstack/util/src/cn'
import type { TooltipProps } from '@mui/material'
import { Tooltip } from '@mui/material'
import map from 'lodash/map'
import type { FunctionComponent } from 'react'
import React from 'react'
import type { AverageTileData } from './RegionGraphs/libs/useDataForAverageTiles'
import { useDataForAverageTiles } from './RegionGraphs/libs/useDataForAverageTiles'

export type RegionAverageTilesCustomProps = {
  region: string
}

// Must forward ref so MUI Tooltip (v4) can attach event handlers & focus/hover logic.
const StyledTile = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...rest }, ref) => (
  <div
    ref={ref}
    className={cn(
      'p-px-24 flex flex-wrap flex-col justify-between font-semibold text-accentroWhite-full bg-accentroDarkGray-full',
      className
    )}
    {...rest}
  />
))
StyledTile.displayName = 'StyledTile'
const StyledGrid: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({
  className,
  ...rest
}) => (
  <div
    className={cn(
      'grid grid-cols-1 sm:grid-cols-2 ml:grid-cols-4 gap-px-8',
      className
    )}
    {...rest}
  />
)

export const RegionAverageTiles: FunctionComponent<
  React.PropsWithChildren<{
    customProps: RegionAverageTilesCustomProps
  }>
> = ({ customProps }) => {
  const t = useTranslation()
  const data = useDataForAverageTiles(customProps.region)
  const oldBuildings = data.slice(0, 4)
  const newBuildings = data.slice(4, 8)
  const tilesNewBuildings = map(newBuildings, (d, idx) => (
    <Tile key={idx} data={d} showTooltip={idx > 1} />
  ))
  const tilesOldBuildings = map(oldBuildings, (d, idx) => (
    <Tile key={idx} data={d} showTooltip={idx > 1} />
  ))
  return (
    <>
      <div className="hidden sm:block">
        <HeadingH2 className="mb-px-48">
          {t(
            'accentro:districtLandingPageGraphs.regionAverageGraph.existingBuildings'
          )}
        </HeadingH2>
        <StyledGrid>{tilesOldBuildings}</StyledGrid>
        <HeadingH2 className="mb-px-48 mt-px-80 ml:mt-px-96">
          {t('accentro:districtLandingPageGraphs.regionAverageGraph.newBuilds')}
        </HeadingH2>
        <StyledGrid>{tilesNewBuildings}</StyledGrid>
      </div>
      <div className="block sm:hidden">
        <SimpleTabs
          tabs={[
            {
              title: t(
                'accentro:districtLandingPageGraphs.regionAverageGraph.existingBuildings'
              ),
              content: <StyledGrid>{tilesOldBuildings}</StyledGrid>,
            },
            {
              title: t(
                'accentro:districtLandingPageGraphs.regionAverageGraph.newBuild'
              ),
              content: <StyledGrid>{tilesNewBuildings}</StyledGrid>,
            },
          ]}
        />
      </div>
    </>
  )
}

const ToolTipContent: FunctionComponent<
  React.PropsWithChildren<{ data: AverageTileData }>
> = ({ data }) => {
  const valueIsPositive =
    typeof data?.relativeChangeTotal === 'number' &&
    data?.relativeChangeTotal > 0
  const formatToDecimal = useFormatToDecimal()
  const t = useTranslation()
  return (
    <div className="p-px-16 flex flex-wrap flex-col justify-between font-semibold text-accentroWhite-full">
      <Text_16_24 className="mb-px-32">{`${data.label} ${t(
        'accentro:districtLandingPageGraphs.regionAverageGraph.inTotal'
      )}`}</Text_16_24>
      <div className="flex flex-row justify-between">
        <div className="flex items-baseline">
          <Text_40_50>{data.totalValue}</Text_40_50>
        </div>
        {typeof data.relativeChangeTotal === 'number' && (
          <div className="flex items-center">
            {valueIsPositive ? (
              <ArrowUpRight className="h-px-32 w-px-32 text-accentroLightGreen-full" />
            ) : (
              <ArrowDownRight className="h-px-32 w-px-32 text-accentroError-full" />
            )}
            <Text_16_24
              className={clsxMerge(
                'ml-px-4',
                valueIsPositive
                  ? 'text-accentroLightGreen-full'
                  : 'text-accentroError-full'
              )}
            >{`${valueIsPositive ? '+' : ''}${formatToDecimal(
              data.relativeChangeTotal
            )}%`}</Text_16_24>
          </div>
        )}
      </div>
    </div>
  )
}

export const Tile: FunctionComponent<
  React.PropsWithChildren<{
    data: AverageTileData
    showTooltip?: boolean
  }>
> = ({ data, showTooltip }) => {
  const Wrapper: any = showTooltip ? Tooltip : 'span'
  const wrapperProps = showTooltip
    ? { title: <ToolTipContent data={data} />, arrow: true }
    : { className: 'inline-block' }
  const formatToDecimal = useFormatToDecimal()
  const formatToCutoff = useFormatToCurrencyCutoff()
  const format =
    data.formatType === 'decimal' ? formatToDecimal : formatToCutoff
  const valueIsPositive = data.relativeChange >= 0
  return (
    <Wrapper {...(wrapperProps as TooltipProps)}>
      <StyledTile>
        <Text_16_24 className="mb-px-32">{data.label}</Text_16_24>
        <div className="flex flex-col">
          <div className="flex items-baseline">
            <Text_40_50>{format(data.value)}</Text_40_50>
            {data.unit && (
              <Text_24_36 className="ml-px-8">{data.unit}</Text_24_36>
            )}
          </div>
          <div className="flex items-center">
            {valueIsPositive ? (
              <ArrowUpRight className="h-px-32 w-px-32 text-accentroLightGreen-full" />
            ) : (
              <ArrowDownRight className="h-px-32 w-px-32 text-accentroError-full" />
            )}
            <Text_16_24
              className={cn(
                'ml-px-4',
                valueIsPositive
                  ? 'text-accentroLightGreen-full'
                  : 'text-accentroError-full'
              )}
            >{`${data.relativeChange > 0 ? '+' : ''}${formatToDecimal(
              data.relativeChange
            )}%`}</Text_16_24>
          </div>
        </div>
      </StyledTile>
    </Wrapper>
  )
}
